import type {
  FlowSetting,
  FlowSettingView,
  FlowSettingWrite,
  FlowSlot,
  ProjectFlowSettings,
} from "@/api/flowSettings";

/**
 * The pure projection of the typed flow contract (#854) into the shapes #855's
 * Pathways and Rules controls render.
 *
 * ## What this module may know, and what it must not
 *
 * It DERIVES no clinical decision and INVENTS no limit. Every ordering it
 * returns is the server's own — `selection.slots` in slot order,
 * `automaticTracks` in the priority order the service computed — and the one
 * bound it reports is `fillableTrackCount`, read off the response, because the
 * pinned loader's own rule is `autoSlotCount <= fillableTrackCount`. The
 * committed contract is explicit that three slots is the MOD/NII configuration
 * and not a product rule, so a literal three in the browser is exactly the
 * defect the parity audit recorded as PW-PRJ-10 (a control that "declares max
 * 2 but persists 3"), one pack revision away.
 *
 * It also never decides that a setting is inherited, authored or removed: that
 * is `FlowSettingView.state`, four values the server distinguishes because the
 * runtime distinguishes them, and a browser that folded `deleted` into
 * `inherited` would tell a clinician the base pack supplies a key the pinned
 * loader will never see.
 *
 * ## The draft is a LIST OF SLOTS, not a count
 *
 * Lovable's "Max dynamic pathways per patient" is one number. In the slots
 * shape the loader DERIVES that number from the slot list, and the legacy
 * `max_dynamic_tracks` key is the obsolete spelling the contract removes
 * through a tombstone. So the field edits the LIST — appending an automatic
 * slot, or dropping the trailing one — and the obsolete key gets Remove /
 * Restore actions of its own. Editing the count as a scalar would either write
 * the obsolete key (refused: it "takes no new value here") or silently claim a
 * limit the slot list contradicts.
 */

/** The engine's own spelling for a slot that classification fills. */
export const AUTO_SLOT = "auto";

const PRIORITY_ADDRESS_PREFIX = "rules.track_priority.";

/** One setting's full editorial state, or `null` when the response omits it. */
export function settingOf(
  state: ProjectFlowSettings,
  setting: FlowSetting,
): FlowSettingView | null {
  return state.settings.find((view) => view.setting === setting) ?? null;
}

/**
 * The track one `trackPriority` entry is about.
 *
 * Off the ADDRESS the server published rather than off a parallel field,
 * because the address is the contract's own join between an entry and the draft
 * that stores it. A track name is `[a-z0-9_]+` and carries no dot, so the
 * suffix is unambiguous; an entry whose address does not have this prefix is
 * not a per-track entry and is reported as `null` rather than guessed at.
 */
export function priorityTrackOf(view: FlowSettingView): string | null {
  if (!view.address.startsWith(PRIORITY_ADDRESS_PREFIX)) {
    return null;
  }
  const track = view.address.slice(PRIORITY_ADDRESS_PREFIX.length);
  return track.length > 0 && !track.includes(".") ? track : null;
}

/** One editable dynamic-phase slot. `track` is set exactly when pinned. */
export type SlotDraft = {
  readonly kind: FlowSlot["kind"];
  readonly track: string | null;
};

/** The slot list the response describes, as the editable draft starts out. */
export function slotDraftOf(state: ProjectFlowSettings): SlotDraft[] {
  return state.selection.slots.map((slot) => ({
    kind: slot.kind,
    track: slot.kind === "always" ? (slot.track ?? null) : null,
  }));
}

/**
 * How many dynamic tracks this Project runs per patient RIGHT NOW, whichever
 * selection shape it is in — or `null` when the shape is unresolved.
 *
 * The correction round 1 needed: a default Project is in the LEGACY shape,
 * where `selection.slots` is empty and the limit lives in the obsolete
 * `max_dynamic_tracks` key. Reading the field off the slot list alone showed a
 * confident `0` and the sentence "this project runs no dynamic phase at all"
 * for a Project whose runtime limit is 2 and whose adopted Pathways are live.
 * `selection.maxDynamicTracks` is the service's own answer for both shapes —
 * the slot count in the slots shape, the literal legacy key otherwise — so it
 * is what the field shows.
 */
export function servedLimit(state: ProjectFlowSettings): number | null {
  return state.selection.maxDynamicTracks ?? null;
}

/**
 * The slot list an EDIT starts from.
 *
 * In the slots shape that is the served list. In the LEGACY shape there is no
 * list yet, so an edit begins by expressing the limit the Project already runs
 * as that many automatic slots — which is the same interview, written in the
 * shape the pinned loader reads — and the author then changes it. Seeding from
 * zero instead made every first edit on a default Project either a refusal
 * ("this project has no dynamic phase slots") or a silent reduction of its
 * limit to whatever the author happened to type first.
 *
 * This is a SEED, not a pending change: it is used only once an edit is made,
 * so a Project nobody has touched stores nothing and reports nothing dirty.
 */
export function slotEditBase(state: ProjectFlowSettings): SlotDraft[] {
  const served = slotDraftOf(state);
  if (served.length > 0 || state.selection.shape !== "legacy") {
    return served;
  }
  const limit = servedLimit(state) ?? 0;
  return Array.from({ length: Math.max(limit, 0) }, () => ({
    kind: "automatic" as const,
    track: null,
  }));
}

/** The wire value one slot draft becomes: track names and `auto`, in order. */
export function slotValue(slots: readonly SlotDraft[]): string[] {
  return slots.map((slot) =>
    slot.kind === "always" && slot.track !== null ? slot.track : AUTO_SLOT,
  );
}

export function slotsEqual(
  left: readonly SlotDraft[],
  right: readonly SlotDraft[],
): boolean {
  const a = slotValue(left);
  const b = slotValue(right);
  return a.length === b.length && a.every((entry, index) => entry === b[index]);
}

/** How many slots classification fills. The loader's `autoSlotCount`. */
export function autoSlotCount(slots: readonly SlotDraft[]): number {
  return slots.filter((slot) => slot.kind === "automatic").length;
}

/** The tracks the slot list pins, in slot order. */
export function alwaysTracks(slots: readonly SlotDraft[]): string[] {
  return slots.flatMap((slot) =>
    slot.kind === "always" && slot.track !== null ? [slot.track] : [],
  );
}

/**
 * A change that could not be made, with the sentence that says what to do
 * instead. `null` is a change that was made.
 *
 * Every arm names an action the author can take on THIS screen: the whole point
 * of refusing here rather than at the service is that a slot list which cannot
 * be built is not worth a round trip, and the remedy is a control a few pixels
 * away.
 */
export type SlotRefusal = string | null;

export type SlotChange = {
  readonly slots: SlotDraft[];
  readonly refusal: SlotRefusal;
};

/**
 * Turn one track's Required switch on or off.
 *
 * ON takes the FIRST automatic slot and pins it, which is what keeps the
 * dynamic limit fixed while the selection moves — "the Always selection
 * occupies one slot, leaving 2 automatic slots" is the source recipe's own
 * sentence, and a toggle that appended a slot would raise the limit behind the
 * author's back. With no automatic slot free the change is refused, naming the
 * two controls that free one.
 *
 * OFF returns that slot to `auto` in place, so the order of the remaining slots
 * is untouched.
 */
export function toggleAlways(
  slots: readonly SlotDraft[],
  track: string,
  on: boolean,
): SlotChange {
  const held = slots.findIndex(
    (slot) => slot.kind === "always" && slot.track === track,
  );
  if (!on) {
    if (held === -1) {
      return { slots: [...slots], refusal: null };
    }
    return {
      slots: slots.map((slot, index) =>
        index === held ? { kind: "automatic", track: null } : slot,
      ),
      refusal: null,
    };
  }
  if (held !== -1) {
    return { slots: [...slots], refusal: null };
  }
  const free = slots.findIndex((slot) => slot.kind === "automatic");
  if (free === -1) {
    return {
      slots: [...slots],
      refusal:
        slots.length === 0
          ? "This project has no dynamic phase slots. Raise Tracks per interview to add one, then mark this track required."
          : "Every slot is already taken by a required track. Turn one of them off, or raise Tracks per interview, to free a slot.",
    };
  }
  return {
    slots: slots.map((slot, index) =>
      index === free ? { kind: "always", track } : slot,
    ),
    refusal: null,
  };
}

/**
 * Change how many dynamic phase slots one patient runs.
 *
 * Growing appends automatic slots; shrinking drops from the END, and refuses
 * rather than dropping a slot a Pathway pins — un-selecting a clinical decision
 * as a side effect of a number is exactly the silent change this surface exists
 * to prevent. The author turns that Pathway's switch off first, which is one
 * click and says what it does.
 */
export function resizeSlots(
  slots: readonly SlotDraft[],
  count: number,
  /**
   * The largest list this Project's registered tracks can fill
   * (`maxSlotCount`), or `null` where it could not be derived.
   *
   * BOTH ENDS OF THE RANGE ARE THE LOADER'S. Below one it refuses `empty_slots`
   * and above this it refuses `insufficient_automatic_tracks` — round 2 closed
   * the first and left the second, so the field's `max` attribute was the only
   * thing between a typed 25 and a document that could only be rejected. An
   * HTML attribute is a hint, not a gate.
   */
  bound: number | null = null,
): SlotChange {
  if (!Number.isInteger(count) || count < 0) {
    return {
      slots: [...slots],
      refusal: "The maximum is a whole number of pathways.",
    };
  }
  if (count === 0) {
    // The pinned loader refuses an empty slot list outright (`empty_slots`), so
    // accepting a zero here would build a document whose only outcome is that
    // refusal — the affordance ADR 0001 forbids. The way to run no dynamic
    // phase is not an empty list; it is a question for whoever owns the pack.
    return {
      slots: [...slots],
      refusal:
        "A project runs at least one dynamic phase slot: the pinned engine refuses an empty list rather than reading it as “no dynamic phase”. Remove the pathways instead, or raise this back to one.",
    };
  }
  if (bound !== null && count > bound) {
    return {
      slots: [...slots],
      refusal: `This project registers ${bound} dynamic ${
        bound === 1 ? "pathway" : "pathways"
      }, so it cannot run more than ${bound} slots: the pinned engine refuses more automatic slots than there are pathways left to fill them. Give another pathway a priority first, or keep this at ${bound} or below.`,
    };
  }
  if (count === slots.length) {
    return { slots: [...slots], refusal: null };
  }
  if (count > slots.length) {
    const added: SlotDraft[] = Array.from(
      { length: count - slots.length },
      () => ({ kind: "automatic", track: null }),
    );
    return { slots: [...slots, ...added], refusal: null };
  }
  const dropped = slots.slice(count);
  const pinned = dropped.flatMap((slot) =>
    slot.kind === "always" && slot.track !== null ? [slot.track] : [],
  );
  if (pinned.length > 0) {
    return {
      slots: [...slots],
      refusal: `Lowering Tracks per interview to ${count} would drop ${
        pinned.length === 1 ? "a slot" : "slots"
      } held by ${pinned.join(", ")}. Turn ${
        pinned.length === 1 ? "that pathway" : "those pathways"
      } off first, so the selection changes deliberately rather than as a side effect.`,
    };
  }
  return { slots: slots.slice(0, count), refusal: null };
}

/**
 * The largest slot list this Project's registered tracks can fill.
 *
 * The pinned loader refuses `autoSlotCount > fillableTrackCount`, so the bound
 * is the whole track pool: the tracks that can fill an automatic slot plus the
 * ones a slot already pins. BOTH HALVES COME FROM THE SERVED SELECTION, and
 * that is the correction: `fillableTrackCount` is derived from the saved
 * document, so adding the PENDING draft's pinned tracks to it counts a track
 * twice — a pending pin moves a track out of the fallback pool the served count
 * still includes, and the bound wrongly grew by one for every switch turned on
 * before a save. The pool itself does not move when a slot changes kind, which
 * is exactly why it is the bound.
 *
 * Nothing here is a constant: a pack that registers a fourth track raises this
 * without a browser change, which is the defect the parity audit recorded as
 * PW-PRJ-10.
 */
export function maxSlotCount(state: ProjectFlowSettings): number {
  return (
    state.selection.fillableTrackCount + state.selection.alwaysTracks.length
  );
}

/**
 * The writes one slot draft becomes, or an empty list when nothing moved.
 *
 * `set` only. Restoring the inherited list and removing a key are separate
 * explicit actions with their own confirmations, because both DISCARD authored
 * state and neither is expressible as "the value I happened to leave in the
 * form".
 */
export function slotWrites(
  state: ProjectFlowSettings,
  slots: readonly SlotDraft[],
): FlowSettingWrite[] {
  if (slotsEqual(slots, slotDraftOf(state))) {
    return [];
  }
  return [
    { setting: "dynamicTrackSlots", op: "set", value: slotValue(slots) },
  ];
}

/**
 * One staged removal or restore, held until Save.
 *
 * NOT WRITTEN ON ITS OWN, and that is a contract fact rather than a UX
 * preference. A default Project starts in the pinned loader's LEGACY selection
 * shape — `default_tracks` and `max_dynamic_tracks` inherited, no slot list —
 * and the loader refuses a document that declares both shapes or neither. So
 * the only path from that state to `[ptsd, auto, auto]` is ONE request that
 * authors the slots and removes the two obsolete keys together: removing either
 * alone leaves an incomplete shape, and authoring the slots alone leaves a
 * conflicting one. Both are refused, correctly, by the service.
 *
 * They stay SEPARATE ACTIONS — a Remove is not a Restore and neither is an
 * edit — and they are confirmed together at Save, where what they discard can
 * be named.
 */
export type StagedChange = {
  readonly setting: FlowSetting;
  readonly track: string | null;
  readonly op: "delete" | "reset";
  /** What the author sees named in the confirmation. Never an engine key. */
  readonly subject: string;
};

/** The stable identity of the address one staged change targets. */
export function stagedKey(setting: FlowSetting, track: string | null): string {
  return track === null ? setting : `${setting}:${track}`;
}

export function stagedWrites(
  staged: ReadonlyMap<string, StagedChange>,
): FlowSettingWrite[] {
  return [...staged.values()].map((change) => ({
    setting: change.setting,
    op: change.op,
    ...(change.track !== null ? { track: change.track } : {}),
  }));
}

/**
 * Which of the two ways back this setting can offer, from the SERVER's own
 * state and delete allowlist.
 *
 * `remove` is offered for an INHERITED key too, which is not a mistake: a
 * tombstone over an inherited key is the whole point of the governed removal —
 * the base pack supplies `default_tracks`, and the Project's way of not running
 * it is to remove it. `restore` is what has no meaning while a setting is
 * inherited: there is no authored value to drop.
 */
export function wayBack(view: FlowSettingView): {
  readonly restore: boolean;
  readonly remove: boolean;
} {
  if (view.state === "authored_above") {
    // The value comes from a WHOLESALE override at an ancestor address. The
    // typed write is refused beside it, and the reset that reaches it is the
    // one at that address — so neither control here would do anything.
    return { restore: false, remove: false };
  }
  return {
    restore: view.state === "authored" || view.state === "deleted",
    remove: view.deletable && view.state !== "deleted",
  };
}

/** One track's priority as it is being edited: the text, exactly as typed. */
export type PriorityDraft = ReadonlyMap<string, string>;

/**
 * The priority text one entry starts from — the EFFECTIVE value, which is what
 * the runtime reads, and an empty string where no layer supplies one.
 */
export function priorityText(view: FlowSettingView): string {
  const value = view.effective.present ? view.effective.value : null;
  return typeof value === "number" ? String(value) : "";
}

export type PriorityWriteResult = {
  readonly writes: FlowSettingWrite[];
  /** Field-addressed refusals, keyed by track. Empty when every entry parses. */
  readonly refusals: ReadonlyMap<string, string>;
};

/**
 * The writes the edited priority entries become.
 *
 * ONE WRITE PER CHANGED TRACK, because the pinned engine deep-merges mappings:
 * sending the map whole would silently keep every base entry the author did not
 * type, which is the defect the per-track address exists to prevent.
 *
 * A blank field is not a removal. Removing an inherited priority is a tombstone
 * with its own control and its own confirmation, and treating an emptied input
 * as one would make a stray backspace a runtime change.
 */
export function priorityWrites(
  entries: readonly FlowSettingView[],
  draft: PriorityDraft,
): PriorityWriteResult {
  const writes: FlowSettingWrite[] = [];
  const refusals = new Map<string, string>();
  for (const view of entries) {
    const track = priorityTrackOf(view);
    if (track === null) {
      continue;
    }
    const typed = draft.get(track);
    if (typed === undefined || typed === priorityText(view)) {
      continue;
    }
    if (typed.trim() === "") {
      refusals.set(
        track,
        "A priority cannot be blank. Type a whole number, or use Restore inherited to go back to the base pack's value.",
      );
      continue;
    }
    const parsed = Number(typed);
    if (!Number.isInteger(parsed) || parsed < 0) {
      refusals.set(
        track,
        "A priority is a whole number, zero or greater: lower runs earlier in the fallback order.",
      );
      continue;
    }
    writes.push({ setting: "trackPriority", op: "set", track, value: parsed });
  }
  // A track with no entry yet (the pack gives it no priority) is written when the author numbers it.
  const known = new Set(entries.map(priorityTrackOf));
  for (const [track, typed] of draft) {
    if (known.has(track) || typed.trim() === "") {
      continue;
    }
    const parsed = Number(typed);
    if (!Number.isInteger(parsed) || parsed < 0) {
      refusals.set(
        track,
        "A priority is a whole number, zero or greater: lower runs earlier in the fallback order.",
      );
      continue;
    }
    writes.push({ setting: "trackPriority", op: "set", track, value: parsed });
  }
  return { writes, refusals };
}

/**
 * The languages a Project may support: the base pack's own list.
 *
 * A Project may only NARROW it — language support is an engine property, and
 * content alone cannot make a new language work — so the control offers exactly
 * these and nothing else.
 */
export function languageChoices(state: ProjectFlowSettings): string[] {
  return [...state.languages.baseSupported];
}

/**
 * Whether "no opening language" is a state this Project can actually reach.
 *
 * Only when the BASE PACK supplies none. `default_language` is not on the
 * pinned engine's delete allowlist, so there is no tombstone for it, and `set`
 * takes a code — which leaves `reset` as the only op that can produce absence,
 * and `reset` produces absence only if the base is absent too. Offering the
 * choice anywhere else is a control whose single outcome is that nothing
 * happens.
 */
export function canClearDefaultLanguage(state: ProjectFlowSettings): boolean {
  return settingOf(state, "defaultLanguage")?.inherited.present === false;
}

export function languageWrites(
  state: ProjectFlowSettings,
  {
    supported,
    defaultLanguage,
  }: { supported: readonly string[]; defaultLanguage: string | null },
): FlowSettingWrite[] {
  const writes: FlowSettingWrite[] = [];
  const current = state.languages.supported;
  const changed =
    supported.length !== current.length ||
    supported.some((code, index) => code !== current[index]);
  if (changed) {
    writes.push({
      setting: "supportedLanguages",
      op: "set",
      value: [...supported],
    });
  }
  // `?? null` on BOTH sides: the wire omits an absent default rather than
  // sending null, so an undefined served value and a null draft are the same
  // fact and must not produce a write that changes nothing.
  const servedDefault = state.languages.default ?? null;
  if (defaultLanguage === servedDefault) {
    return writes;
  }
  if (defaultLanguage !== null) {
    writes.push({
      setting: "defaultLanguage",
      op: "set",
      value: defaultLanguage,
    });
    return writes;
  }
  // CLEARING IT IS A RESET, AND ONLY WHERE A RESET CLEARS IT. Round 1 emitted
  // nothing here: choosing "No default language" over a served one produced an
  // empty write list, so Save was either disabled (looking like the choice had
  // been accepted and was simply not a change) or carried the OTHER edits while
  // the old default silently stood. Both are the control lying about what it
  // did. Where the base supplies a default, absence is unrepresentable and the
  // choice is not offered at all (`canClearDefaultLanguage`).
  if (canClearDefaultLanguage(state)) {
    writes.push({ setting: "defaultLanguage", op: "reset" });
  }
  return writes;
}

/**
 * The display name for a language code.
 *
 * `Intl.DisplayNames` rather than a table: the base pack decides which codes
 * exist, and a hard-coded map would render a code the pack added as a blank.
 * The CODE is always shown beside the name, because the code is what the
 * runtime and the exported document carry.
 */
export function languageName(code: string): string {
  try {
    const names = new Intl.DisplayNames(["en"], { type: "language" });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Whether this state has anything the pinned loader would refuse. */
export function hasFindings(state: ProjectFlowSettings): boolean {
  return state.findings.length > 0;
}

/**
 * The label for one setting's provenance, in the vocabulary the server uses.
 *
 * `authored_above` is spelled out rather than folded into `authored`: it means
 * a WHOLESALE draft override at an ancestor address supplies this setting
 * without naming it, the typed write is refused beside it, and the remedy is to
 * revert that override — three facts a shared word would hide.
 */
export const FLOW_STATE_LABEL = {
  inherited: "Inherited",
  authored: "Set in this project",
  deleted: "Removed in this project",
  authored_above: "Supplied by a wholesale override",
} as const satisfies Record<FlowSettingView["state"], string>;
